// app/javascript/controllers/gift_trash_animation_controller.js
import { Controller } from "@hotwired/stimulus"
import { gsap } from "gsap"

export default class extends Controller {
    static targets = ["stage", "giftBox", "trashCan", "item"]

    connect() {
        if (this.started) return
        this.started = true

        this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
        this.smallLayout = window.matchMedia("(max-width: 1399.98px)").matches

        if (this.reducedMotion || this.smallLayout) {
            this.hideItems()
            return
        }

        requestAnimationFrame(() => {
            if (!this.element?.isConnected) return
            this.buildAnimation()
        })
    }

    disconnect() {
        this.destroyAnimation()
        this.started = false
    }

    hideItems() {
        this.itemTargets.forEach((item) => {
            gsap.set(item, { autoAlpha: 0 })
        })
    }

    destroyAnimation() {
        if (this.timeline) {
            this.timeline.kill()
            this.timeline = null
        }

        if (this.itemTargets?.length) {
            gsap.killTweensOf(this.itemTargets)
        }
    }

    buildAnimation() {
        this.destroyAnimation()

        if (
            !this.hasStageTarget ||
            !this.hasGiftBoxTarget ||
            !this.hasTrashCanTarget ||
            this.itemTargets.length === 0
        ) {
            return
        }

        const stageRect = this.stageTarget.getBoundingClientRect()
        const giftRect = this.giftBoxTarget.getBoundingClientRect()
        const trashRect = this.trashCanTarget.getBoundingClientRect()

        if (
            !stageRect.width || !stageRect.height ||
            !giftRect.width || !giftRect.height ||
            !trashRect.width || !trashRect.height
        ) {
            return
        }

        // Move spawn further LEFT so objects feel like they emerge from the box opening
        const startX = giftRect.left - stageRect.left + (giftRect.width * 0.42)
        const startY = giftRect.top - stageRect.top + (giftRect.height * 0.30)

        // Drop a little deeper into trash opening
        const endX = trashRect.left - stageRect.left + (trashRect.width * 0.42)
        const endY = trashRect.top - stageRect.top + (trashRect.height * 0.18)

        const configs = [
            // ribbon
            { scale: 1.25, mid1x: -18, mid1y: -35, mid2x: 10,  mid2y: -92,  rotation: -150, duration: 2.25 },
            // wrapped gift
            { scale: 0.84, mid1x: -12, mid1y: -34, mid2x: 20,  mid2y: -112, rotation: 140,  duration: 2.40 },
            // teddy
            { scale: 0.89, mid1x: -18, mid1y: -38, mid2x: 8,   mid2y: -104, rotation: -125, duration: 2.38 },
            // car
            { scale: 0.90, mid1x: -8,  mid1y: -26, mid2x: 24,  mid2y: -94,  rotation: 180,  duration: 2.22 },
            // robot
            { scale: 0.70, mid1x: -10, mid1y: -34, mid2x: 16,  mid2y: -106, rotation: -160, duration: 2.40 },
            // dollar last
            { scale: 1.25, mid1x: -4,  mid1y: -20, mid2x: 28,  mid2y: -82,  rotation: 210,  duration: 2.10 }
        ]

        const spacing = 0.58
        const fadeOutDuration = 0.10

        this.itemTargets.forEach((item, index) => {
            const cfg = configs[index % configs.length]

            gsap.set(item, {
                x: startX,
                y: startY,
                scale: cfg.scale,
                rotation: 0,
                autoAlpha: 0,
                force3D: true,
                transformOrigin: "50% 50%"
            })
        })

        const tl = gsap.timeline({
            repeat: -1,
            paused: false
        })

        this.itemTargets.forEach((item, index) => {
            const cfg = configs[index % configs.length]
            const launchAt = index * spacing
            const impactAt = launchAt + cfg.duration

            tl.set(item, {
                x: startX,
                y: startY,
                scale: cfg.scale,
                rotation: 0,
                autoAlpha: 0
            }, launchAt)

            tl.to(item, {
                autoAlpha: 1,
                duration: 0.05,
                ease: "power1.out"
            }, launchAt)

            tl.to(item, {
                x: startX + cfg.mid1x,
                y: startY + cfg.mid1y,
                rotation: cfg.rotation * 0.18,
                duration: cfg.duration * 0.22,
                ease: "power1.out"
            }, launchAt)

            tl.to(item, {
                x: startX + ((endX - startX) * 0.44) + cfg.mid2x,
                y: startY + cfg.mid2y,
                rotation: cfg.rotation * 0.62,
                duration: cfg.duration * 0.36,
                ease: "sine.out"
            }, launchAt + (cfg.duration * 0.22))

            tl.to(item, {
                x: endX,
                y: endY,
                rotation: cfg.rotation,
                scale: cfg.scale * 0.84,
                duration: cfg.duration * 0.42,
                ease: "power2.in"
            }, launchAt + (cfg.duration * 0.58))

            tl.to(item, {
                autoAlpha: 0,
                scale: cfg.scale * 0.25,
                duration: fadeOutDuration,
                ease: "power2.in"
            }, impactAt - 0.02)
        })

        // short quiet gap before looping
        const totalDuration = ((this.itemTargets.length - 1) * spacing) + 2.1
        tl.to({}, { duration: 0.35 }, totalDuration)

        this.timeline = tl
    }
}